import { useEffect, useState } from "react";
import { Button } from "@/shared/ui/Button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/shared/ui/Dialog";
import { Input } from "@/shared/ui/Input";
import type { LedgerAllocationOperationType } from "../../types/accounting.types";

type Props = {
  category: LedgerAllocationOperationType | null
  isBusy: boolean
  onClose: () => void
  onConfirm: (name: string) => void
};

/**
 * ADM-621 - le renommage passe par un dialogue et non par une édition en place.
 *
 * L'inline edit ouvert depuis le menu d'actions ne pouvait pas fonctionner : le
 * menu Radix rend le focus à son trigger en se fermant, ce qui blurait l'input
 * aussitôt et refermait l'édition. Un dialogue supprime la course au focus, et
 * aligne Renommer sur Fusionner et Supprimer, déjà des dialogues.
 */
const RenameCategoryDialog = ({
  category, isBusy, onClose, onConfirm,
}: Props) => {
  const [name, setName] = useState("");

  useEffect(() => {
    setName(category?.name ?? "");
  }, [category]);

  const trimmed = name.trim();
  const isValid = trimmed.length > 0 && trimmed !== category?.name;

  return (
    <Dialog open={category !== null} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Renommer la catégorie</DialogTitle>
          <DialogDescription>
            Les écritures déjà affectées suivent le nouveau nom, rien n'est décatégorisé.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2">
          <Input
            autoFocus
            aria-label="Nom de la catégorie"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder="Nom de la catégorie"
            onKeyDown={(event) => {
              if (event.key === "Enter" && isValid) onConfirm(trimmed);
            }}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isBusy}>Annuler</Button>
          <Button onClick={() => onConfirm(trimmed)} disabled={!isValid || isBusy}>Renommer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RenameCategoryDialog;
